/**
 * PRO-4 wizard: form shape and Yup validation, mirroring the backend rules (docs/06 §4.1) so a
 * form that passes here can only fail server-side on cross-record rules.
 *
 * The form values are all STRINGS, including the numeric ones. That is deliberate:
 *   • Money must never round-trip through a JS float (CLAUDE.md), and an `<input type=number>`
 *     hands you a `number`.
 *   • A half-typed "1." or an empty field is a legitimate intermediate state; coercing it to
 *     `NaN` or `0` mid-keystroke makes the live previews flicker garbage.
 * They are parsed once, at submit, in `toProductInput`.
 */
import * as Yup from 'yup';
import type { TFunction } from 'i18next';
import type { CapacityUnit, PriceUnit, ProductInput } from '@/shared/contract';

export interface WizardValues {
  // Step 1 — basics
  name: string;
  category_id: string;
  description: string;
  composition: string;
  tnved_code: string;
  country_of_origin: string;
  capacity_value: string;
  capacity_unit: CapacityUnit | '';

  // Step 2 — packaging chain
  unit_weight_kg: string;
  pieces_per_pack: string;
  packs_per_box: string;
  boxes_per_pallet: string;
  pallets_per_truck: string;

  // Step 3 — the producer's own base price
  producer_price_value: string;
  producer_price_unit: PriceUnit | '';
}

export const EMPTY_WIZARD: WizardValues = {
  name: '',
  category_id: '',
  description: '',
  composition: '',
  tnved_code: '',
  country_of_origin: 'TM',
  capacity_value: '',
  capacity_unit: '',
  unit_weight_kg: '',
  // Integer multipliers start at 1, the smallest valid value; the producer overwrites them.
  pieces_per_pack: '1',
  packs_per_box: '1',
  boxes_per_pallet: '1',
  pallets_per_truck: '1',
  producer_price_value: '',
  producer_price_unit: '',
};

/** A positive integer ≥ 1, as a string. Rejects "1.5", "0", "-2", "abc". */
const intMin1 = (t: TFunction, msg?: string) =>
  Yup.string()
    .required(t('validation.required'))
    .test('int-min-1', msg ?? t('validation.integerMin1'), (v) => {
      if (!v) return false;
      if (!/^\d+$/.test(v.trim())) return false;
      return Number.parseInt(v, 10) >= 1;
    });

/** Like `intMin1`, but an empty value is allowed (the field is optional). */
const optionalIntMin1 = (t: TFunction) =>
  Yup.string().test('int-min-1', t('validation.integerMin1'), (v) => {
    if (!v || !v.trim()) return true;
    if (!/^\d+$/.test(v.trim())) return false;
    return Number.parseInt(v, 10) >= 1;
  });

/** A positive decimal, as a string. Accepts "0.25" and "0,25" (RU keyboards produce commas). */
const positiveDecimal = (t: TFunction) =>
  Yup.string()
    .required(t('validation.required'))
    .test('positive-decimal', t('validation.positive'), (v) => {
      if (!v) return false;
      const n = Number.parseFloat(v.trim().replace(',', '.'));
      return Number.isFinite(n) && n > 0;
    });

export function wizardSchema(t: TFunction) {
  return Yup.object({
    name: Yup.string().trim().required(t('validation.required')),
    category_id: Yup.string().required(t('validation.required')),
    description: Yup.string(),
    composition: Yup.string(),
    // Required by activation (CanActivate on the server), so it is required in the form too.
    tnved_code: Yup.string().trim().required(t('validation.required')),
    country_of_origin: Yup.string()
      .trim()
      .length(2, t('validation.country2'))
      .required(t('validation.required')),

    // Capacity is required for ACTIVATION (docs/04 §1.2 preconditions), but a draft may be saved
    // without it — the step-level schemas below enforce the activation bar.
    capacity_value: Yup.string(),
    capacity_unit: Yup.string(),

    unit_weight_kg: positiveDecimal(t),
    pieces_per_pack: intMin1(t),
    // Optional: unstated means one pack per box / the 33-pallet reference truck (server-side).
    packs_per_box: optionalIntMin1(t),
    boxes_per_pallet: intMin1(t),
    pallets_per_truck: optionalIntMin1(t),

    producer_price_value: positiveDecimal(t),
    // NEVER `truck` — a producer prices against piece/pack/box/pallet only (openapi `PriceUnit`).
    producer_price_unit: Yup.string()
      .oneOf(['piece', 'pack', 'box', 'pallet'], t('validation.required'))
      .required(t('validation.required')),
  });
}

/** Fields each step owns, so "Next" can validate only the step in front of the user. */
export const STEP_FIELDS: Array<Array<keyof WizardValues>> = [
  ['name', 'category_id', 'description', 'composition', 'tnved_code', 'country_of_origin', 'capacity_value', 'capacity_unit'],
  ['unit_weight_kg', 'pieces_per_pack', 'packs_per_box', 'boxes_per_pallet', 'pallets_per_truck'],
  ['producer_price_value', 'producer_price_unit'],
  [], // media — uploaded directly, not part of the Formik state
  [], // review
];

/**
 * The activation bar (docs/04 §1.2, server-validated): ≥1 photo, a complete packaging chain,
 * weight > 0, base price + unit, monthly capacity, category, and TN VED code.
 *
 * The photo count is NOT in the Formik values (media is uploaded straight to the API), so it is
 * passed in separately.
 */
export function canActivate(values: WizardValues, photoCount: number): boolean {
  if (photoCount < 1) return false;

  const filled = (s: string) => s.trim().length > 0;
  const posInt = (s: string) => /^\d+$/.test(s.trim()) && Number.parseInt(s, 10) >= 1;
  const optPosInt = (s: string) => !filled(s) || posInt(s);
  const posDec = (s: string) => {
    const n = Number.parseFloat(s.trim().replace(',', '.'));
    return Number.isFinite(n) && n > 0;
  };

  return (
    filled(values.name) &&
    filled(values.category_id) &&
    filled(values.country_of_origin) &&
    filled(values.tnved_code) &&
    filled(values.capacity_value) &&
    filled(values.capacity_unit) &&
    posDec(values.unit_weight_kg) &&
    posInt(values.pieces_per_pack) &&
    optPosInt(values.packs_per_box) &&
    posInt(values.boxes_per_pallet) &&
    optPosInt(values.pallets_per_truck) &&
    posDec(values.producer_price_value) &&
    filled(values.producer_price_unit)
  );
}

/** The single parse point: strings → the `ProductInput` the contract expects. */
export function toProductInput(v: WizardValues): ProductInput {
  const dec = (s: string) => s.trim().replace(',', '.');
  const nul = (s: string) => (s.trim() ? s.trim() : null);
  const optInt = (s: string) => (s.trim() ? Number.parseInt(s, 10) : null);

  return {
    name: v.name.trim(),
    description: nul(v.description),
    composition: nul(v.composition),
    category_id: v.category_id,
    tnved_code: nul(v.tnved_code),
    country_of_origin: v.country_of_origin.trim().toUpperCase(),
    capacity_value: v.capacity_value.trim() ? dec(v.capacity_value) : null,
    capacity_unit: v.capacity_unit ? (v.capacity_unit as CapacityUnit) : null,
    unit_weight_kg: dec(v.unit_weight_kg),
    pieces_per_pack: Number.parseInt(v.pieces_per_pack, 10),
    packs_per_box: optInt(v.packs_per_box),
    boxes_per_pallet: Number.parseInt(v.boxes_per_pallet, 10),
    pallets_per_truck: optInt(v.pallets_per_truck),
    // Money stays a decimal STRING all the way to the wire.
    producer_price_value: dec(v.producer_price_value),
    producer_price_unit: v.producer_price_unit as PriceUnit,
  };
}

/** Hydrate the wizard from an existing product (edit mode). */
export function fromProduct(p: {
  name: string;
  description?: string | null;
  composition?: string | null;
  category: { id: string };
  tnved_code?: string | null;
  country_of_origin: string;
  capacity_value?: string | null;
  capacity_unit?: string | null;
  packaging: {
    unit_weight_kg: string;
    pieces_per_pack: number;
    packs_per_box: number | null;
    boxes_per_pallet: number;
    pallets_per_truck: number | null;
  };
  producer_price_value: string;
  producer_price_unit: PriceUnit;
}): WizardValues {
  return {
    name: p.name,
    category_id: p.category.id,
    description: p.description ?? '',
    composition: p.composition ?? '',
    tnved_code: p.tnved_code ?? '',
    country_of_origin: p.country_of_origin,
    capacity_value: p.capacity_value ?? '',
    capacity_unit: (p.capacity_unit as CapacityUnit) ?? '',
    unit_weight_kg: p.packaging.unit_weight_kg,
    pieces_per_pack: String(p.packaging.pieces_per_pack),
    packs_per_box: p.packaging.packs_per_box == null ? '' : String(p.packaging.packs_per_box),
    boxes_per_pallet: String(p.packaging.boxes_per_pallet),
    pallets_per_truck:
      p.packaging.pallets_per_truck == null ? '' : String(p.packaging.pallets_per_truck),
    producer_price_value: p.producer_price_value,
    producer_price_unit: p.producer_price_unit,
  };
}

/**
 * MARGIN GUARD detection (docs/04 §1.2).
 *
 * Editing the base price, price unit, or ANY packaging field of an already-active, priced product
 * sends it back to MOST's pricing queue — and if the new per-piece base price lands at or above
 * the (invisible to us) selling price, the server clears the selling price, which unpublishes the
 * product until Merdan re-prices it.
 *
 * We cannot predict WHICH branch fires: that needs the selling price, which the producer must
 * never have. So we warn about both possibilities and let the server decide. This function only
 * answers "does this edit touch the fields that trigger a re-price?".
 */
export function triggersReprice(before: WizardValues, after: WizardValues): boolean {
  const fields: Array<keyof WizardValues> = [
    'producer_price_value',
    'producer_price_unit',
    'unit_weight_kg',
    'pieces_per_pack',
    'packs_per_box',
    'boxes_per_pallet',
    'pallets_per_truck',
  ];

  return fields.some((f) => before[f].trim() !== after[f].trim());
}
